import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import { useFormik } from "formik";
import * as yup from "yup";
import Labels from "../labels/Labels";

// Helper function to extract error message from backend response
function getErrorMessage(error, defaultMessage) {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  if (error?.response?.data) {
    return typeof error.response.data === "string"
      ? error.response.data
      : defaultMessage;
  }
  if (error?.message) {
    return error.message;
  }
  return defaultMessage;
}

export default function AddPaymentDescriptor(props) {
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  // Mode selection state (0 = Individual, 1 = CSV Upload)
  const [creationMode, setCreationMode] = useState(0);

  // CSV Upload state
  const [csvFile, setCsvFile] = useState(null);
  const [csvFileError, setCsvFileError] = useState("");
  const [csvResults, setCsvResults] = useState(null);
  const [showCsvResults, setShowCsvResults] = useState(false);

  const queryClient = useQueryClient();

  const createPaymentDescriptorMutation = useMutation(
    (values) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/payment-descriptors`,
        values
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("paymentDescriptors");
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "paymentDescriptor.createSuccess",
            "Payment descriptor created successfully"
          ),
        });
        props.handleClose();
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: getErrorMessage(
            error,
            Labels.getLabel(
              "paymentDescriptor.createError",
              "Error creating the payment descriptor"
            )
          ),
        });
      },
    }
  );


  // CSV Upload mutation
  const csvUploadMutation = useMutation(
    (csvData) => {
      const formData = new FormData();
      formData.append("csvFile", csvData.file);
      formData.append("employerId", csvData.employerId);

      return HttpService.getAxiosClient().post(
        window.API_URL + `/payment-descriptors/csv-upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    },
    {
      onSuccess: (response) => {
        const results = response?.data;
        let successMessage;
        let alertType;

        setCsvResults(results);

        if (results?.successCount > 0 && results?.errorCount > 0) {
          successMessage = Labels.getLabel(
            "paymentDescriptor.csv.success.partial",
            `${results.successCount} descriptors created successfully, ${results.errorCount} failed.`
          );
          alertType = "warning";
          setShowCsvResults(true);
        } else if (results?.successCount > 0 && (!results?.errorCount || results.errorCount === 0)) {
          successMessage = Labels.getLabel(
            "paymentDescriptor.csv.success.all",
            `All ${results.successCount} descriptors created successfully!`
          );
          alertType = "success";
        } else if (results?.errorCount > 0 && (!results?.successCount || results.successCount === 0)) {
          successMessage = Labels.getLabel(
            "paymentDescriptor.csv.error.all",
            `Failed to create all ${results.errorCount} descriptors.`
          );
          alertType = "error";
          setShowCsvResults(true);
        } else {
          successMessage = Labels.getLabel(
            "paymentDescriptor.csv.completed",
            "CSV processing completed"
          );
          alertType = "info";
        }

        queryClient.invalidateQueries("paymentDescriptors");
        setFeedback({
          open: true,
          type: alertType,
          message: successMessage,
        });

        if (alertType === "success") {
          setTimeout(() => props.handleClose(), 2000);
        }
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: getErrorMessage(
            error,
            Labels.getLabel(
              "paymentDescriptor.csv.uploadError",
              "Error uploading CSV file"
            )
          ),
        });
      },
    }
  );

  const validationSchema = yup.object({
    idEmployer: yup
      .string()
      .required(
        Labels.getLabel(
          "paymentDescriptor.employerRequired",
          "Employer is required"
        )
      ),
    description: yup
      .string("Enter the description")
      .required(
        Labels.getLabel(
          "paymentDescriptor.descriptionRequired",
          "Description is required"
        )
      )
      .max(
        32,
        Labels.getLabel(
          "paymentDescriptor.descriptionMaxLength",
          "Description must be 32 characters or less"
        )
      ),
    descriptorId: yup
      .number(
        Labels.getLabel(
          "paymentDescriptor.descriptorIdNumber",
          "Descriptor ID must be a number"
        )
      )
      .integer(
        Labels.getLabel(
          "paymentDescriptor.descriptorIdInteger",
          "Descriptor ID must be an integer"
        )
      )
      .nullable(),
    paymentAmount: yup.number().when("isFixedValue", {
      is: true,
      then: (schema) =>
        schema.required(
          Labels.getLabel(
            "paymentDescriptor.paymentAmountRequired",
            "Payment amount is required when Fixed Value is checked"
          )
        ),
      otherwise: (schema) => schema.nullable(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      idEmployer: "",
      idSubdepartment: "",
      description: "",
      descriptorId: null,
      isFixedValue: false, // Checkbox state
      paymentAmount: null,
      avoidMaxYearlyValidation: false,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // Remove null/empty descriptorId to let backend handle it
      const submitData = {
        idEmployer: values.idSubdepartment || values.idEmployer, // Use subdepartment if selected, otherwise employer
        description: values.description,
        descriptorId: values.descriptorId || null,
        amountType: values.isFixedValue ? "FIXED_AMOUNT" : "VARIABLE",
        paymentAmount: values.isFixedValue ? values.paymentAmount : null,
        avoidMaxYearlyValidation: values.avoidMaxYearlyValidation,
      };
      createPaymentDescriptorMutation.mutate(submitData);
    },
  });


  // Handle mode change
  const handleModeChange = (event, newValue) => {
    setCreationMode(newValue);
    formik.resetForm();
    setCsvFile(null);
    setCsvFileError("");
    setCsvResults(null);
    setShowCsvResults(false);
  };

  // Handle CSV file selection
  const handleCsvFileChange = (event) => {
    const file = event.target.files[0];
    setCsvFileError("");

    if (!file) {
      setCsvFile(null);
      return;
    }

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setCsvFileError(
        Labels.getLabel(
          "paymentDescriptor.csv.validation.invalidType",
          "Please select a CSV file"
        )
      );
      setCsvFile(null);
      return;
    }

    // File size validation (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setCsvFileError(
        Labels.getLabel(
          "paymentDescriptor.csv.validation.fileTooLarge",
          "File size must be less than 5MB"
        )
      );
      setCsvFile(null);
      return;
    }

    setCsvFile(file);
  };

  // Handle CSV form submission
  const handleCsvSubmit = () => {
    if (!csvFile) {
      setCsvFileError(
        Labels.getLabel(
          "paymentDescriptor.csv.validation.noFile",
          "Please select a CSV file"
        )
      );
      return;
    }

    if (!formik.values.idEmployer) {
      setCsvFileError(
        Labels.getLabel(
          "paymentDescriptor.csv.validation.noEmployer",
          "Please select an employer"
        )
      );
      return;
    }

    const targetEmployerId = formik.values.idSubdepartment || formik.values.idEmployer;
    console.log("📤 CSV Submit - idEmployer:", formik.values.idEmployer);
    console.log("📤 CSV Submit - idSubdepartment:", formik.values.idSubdepartment);
    console.log("📤 CSV Submit - Using targetEmployerId:", targetEmployerId);
    
    csvUploadMutation.mutate({
      file: csvFile,
      employerId: targetEmployerId,
    });
  };

  // Generate and download CSV template
  const downloadCsvTemplate = () => {
    const csvContent = `descriptorId,description,amountType,paymentAmount
1,Regular Salary,VARIABLE,
2,Bonus Payment,FIXED_AMOUNT,500.00
3,Commission,VARIABLE,`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "payment_descriptors_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  async function fetchCompanyData() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/employer/all?excludeRelations=true`
    );
    return response;
  }

  async function fetchSubdepartments(employerId) {
    console.log("🔍 fetchSubdepartments called with employerId:", employerId);
    if (!employerId) return { data: [] };
    console.log("🌐 Calling subdepartment endpoint for employer:", employerId);
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/subdepartment/options?companyId=${employerId}`,
      {
        headers: {
          "X-Chosen-Employer": employerId,
        },
      }
    );
    console.log("✅ Subdepartments response:", response);
    return response;
  }

  const { data: companiesData, isLoading: isLoadingCompanies } = useQuery(
    ["companies"],
    () => fetchCompanyData(),
    {
      onSuccess: (data) => {
        // Auto-select if only one company available
        if (data?.data?.length === 1 && !formik.values.idEmployer) {
          formik.setFieldValue("idEmployer", data.data[0].idEmployer);
        }
      },
    }
  );

  const { data: subdepartmentsData, isLoading: isLoadingSubdepartments } =
    useQuery(
      ["subdepartments", formik.values.idEmployer],
      () => fetchSubdepartments(formik.values.idEmployer),
      {
        enabled: !!formik.values.idEmployer,
        onSuccess: (data) => {
          console.log("✅ Subdepartments query succeeded:", data);
        },
        onError: (error) => {
          console.error("❌ Subdepartments query failed:", error);
        },
      }
    );

  console.log("🔄 Current idEmployer:", formik.values.idEmployer);
  console.log("🔄 Query enabled:", !!formik.values.idEmployer);
  console.log("🔄 Subdepartments data:", subdepartmentsData);

  return (
    <div>
      {/* Mode Selection Tabs */}
      <Tabs
        value={creationMode}
        onChange={handleModeChange}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        <Tab
          label={Labels.getLabel(
            "paymentDescriptor.tabs.individual",
            "Individual Descriptor"
          )}
        />
        <Tab
          label={Labels.getLabel(
            "paymentDescriptor.tabs.csvUpload",
            "CSV Upload"
          )}
        />
      </Tabs>

      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={creationMode === 0 ? formik.handleSubmit : (e) => e.preventDefault()}
      >
        {creationMode === 0 ? (
          // Individual Descriptor Form
          isLoadingCompanies ? (
            Labels.getLabel(
              "paymentDescriptor.loadingCompanies",
              "Loading companies..."
            )
          ) : (
            <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                id="idEmployer"
                options={companiesData?.data || []}
                getOptionLabel={(option) =>
                  `${option.description || option.name} [${option.idEmployer}]`
                }
                value={
                  companiesData?.data?.find(
                    (company) => company.idEmployer === formik.values.idEmployer
                  ) || null
                }
                onChange={(event, selectedOption) => {
                  console.log("🏢 Employer selected:", selectedOption);
                  const newEmployerId = selectedOption
                    ? selectedOption.idEmployer
                    : "";
                  console.log("📝 Setting idEmployer to:", newEmployerId);
                  formik.setFieldValue("idEmployer", newEmployerId);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={Boolean(formik.errors.idEmployer)}
                    helperText={formik.errors.idEmployer}
                    label={Labels.getLabel(
                      "paymentDescriptor.employer",
                      "Employer"
                    )}
                    placeholder={Labels.getLabel(
                      "paymentDescriptor.selectEmployer",
                      "Select an employer"
                    )}
                  />
                )}
              />
            </Grid>

            {/* Subdepartment Selection - only show if employer has subdepartments */}
            {formik.values.idEmployer &&
              subdepartmentsData?.data &&
              subdepartmentsData.data.length > 0 && (
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    id="idSubdepartment"
                    options={subdepartmentsData.data}
                    getOptionLabel={(option) => `${option.name} [${option.id}]`}
                    value={
                      subdepartmentsData.data.find(
                        (subdept) =>
                          subdept.id === formik.values.idSubdepartment
                      ) || null
                    }
                    onChange={(event, selectedOption) => {
                      formik.setFieldValue(
                        "idSubdepartment",
                        selectedOption ? selectedOption.id : ""
                      );
                    }}
                    loading={isLoadingSubdepartments}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={Labels.getLabel(
                          "paymentDescriptor.subdepartment",
                          "Organizational Unit (Optional)"
                        )}
                        placeholder={Labels.getLabel(
                          "paymentDescriptor.selectSubdepartment",
                          "Select an organizational unit"
                        )}
                        helperText={Labels.getLabel(
                          "paymentDescriptor.subdepartmentHelp",
                          "Optional. Select an organizational unit if this descriptor is specific to one."
                        )}
                      />
                    )}
                  />
                </Grid>
              )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="descriptorId"
                label={Labels.getLabel(
                  "paymentDescriptor.descriptorId",
                  "Descriptor ID"
                )}
                type="number"
                value={formik.values.descriptorId || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  formik.setFieldValue(
                    "descriptorId",
                    value === "" ? null : parseInt(value, 10)
                  );
                }}
                error={
                  formik.touched.descriptorId &&
                  Boolean(formik.errors.descriptorId)
                }
                helperText={
                  formik.touched.descriptorId && formik.errors.descriptorId
                    ? formik.errors.descriptorId
                    : Labels.getLabel(
                        "paymentDescriptor.descriptorIdOptional",
                        "Optional. If not provided, will be generated automatically."
                      )
                }
                placeholder={Labels.getLabel(
                  "paymentDescriptor.descriptorIdPlaceholder",
                  "e.g., 1, 2, 3..."
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.isFixedValue}
                    onChange={(e) => {
                      formik.setFieldValue("isFixedValue", e.target.checked);
                      // Clear fixedValue when unchecking
                      if (!e.target.checked) {
                        formik.setFieldValue("paymentAmount", null);
                      }
                    }}
                    name="isFixedValue"
                  />
                }
                label={Labels.getLabel(
                  "paymentDescriptor.fixedPaymentAmount",
                  "Fixed Payment Amount"
                )}
              />
            </Grid>
            {formik.values.isFixedValue && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="paymentAmount"
                  label={Labels.getLabel(
                    "paymentDescriptor.paymentAmount",
                    "Payment Amount"
                  )}
                  type="number"
                  value={formik.values.paymentAmount || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue(
                      "paymentAmount",
                      value === "" ? null : parseFloat(value)
                    );
                  }}
                  error={
                    formik.touched.paymentAmount &&
                    Boolean(formik.errors.paymentAmount)
                  }
                  helperText={
                    formik.touched.paymentAmount && formik.errors.paymentAmount
                      ? formik.errors.paymentAmount
                      : Labels.getLabel(
                          "paymentDescriptor.paymentAmountHelper",
                          "Enter the numeric payment amount for this descriptor"
                        )
                  }
                  placeholder={Labels.getLabel(
                    "paymentDescriptor.paymentAmountPlaceholder",
                    "e.g., 100.50"
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="description"
                label={Labels.getLabel(
                  "paymentDescriptor.description",
                  "Description"
                )}
                multiline
                inputProps={{ maxLength: 32 }}
                rows={3}
                value={formik.values.description}
                onChange={formik.handleChange}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
                placeholder={Labels.getLabel(
                  "paymentDescriptor.descriptionPlaceholder",
                  "Enter payment descriptor description"
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.avoidMaxYearlyValidation}
                    onChange={(e) => {
                      formik.setFieldValue("avoidMaxYearlyValidation", e.target.checked);
                    }}
                    name="avoidMaxYearlyValidation"
                  />
                }
                label={Labels.getLabel(
                  "paymentDescriptor.avoidMaxYearlyValidation",
                  "Avoid Max Yearly Validation"
                )}
              />
            </Grid>
            <Grid item xs={12} pt={3}>
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={createPaymentDescriptorMutation.isLoading}
              >
                {createPaymentDescriptorMutation.isLoading
                  ? Labels.getLabel("paymentDescriptor.saving", "SAVING...")
                  : Labels.getLabel("paymentDescriptor.save", "SAVE")}
              </Button>
            </Grid>
          </Grid>
          )
        ) : (
          // CSV Upload Form
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {Labels.getLabel(
                  "paymentDescriptor.csv.instructions",
                  "Upload a CSV file to create multiple payment descriptors at once. Maximum 500 descriptors per upload."
                )}
              </Typography>
            </Grid>

            {/* Employer Selection for CSV */}
            <Grid item xs={12}>
              <Autocomplete
                options={companiesData?.data?.filter((c) => c.idEmployer) || []}
                getOptionLabel={(option) =>
                  `${option.description || option.name || ""} [${option.idEmployer}]`
                }
                filterOptions={(options, state) => {
                  const inputValue = state.inputValue.toLowerCase();
                  return options.filter(
                    (option) =>
                      (option.description && option.description.toLowerCase().includes(inputValue)) ||
                      (option.name && option.name.toLowerCase().includes(inputValue)) ||
                      (option.idEmployer && option.idEmployer.toLowerCase().includes(inputValue))
                  );
                }}
                value={
                  companiesData?.data?.find((c) => c.idEmployer === formik.values.idEmployer) || null
                }
                onChange={(event, newValue) => {
                  formik.setFieldValue("idEmployer", newValue?.idEmployer || "");
                  formik.setFieldValue("idSubdepartment", "");
                }}
                disabled={csvUploadMutation.isLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={Labels.getLabel("paymentDescriptor.employer", "Employer")}
                    required
                    error={formik.touched.idEmployer && Boolean(formik.errors.idEmployer)}
                    helperText={
                      formik.touched.idEmployer && formik.errors.idEmployer
                        ? formik.errors.idEmployer
                        : Labels.getLabel(
                            "paymentDescriptor.csv.employerHelp",
                            "Select the employer for all descriptors in the CSV"
                          )
                    }
                  />
                )}
              />
            </Grid>

            {/* Subdepartment Selection (Optional) */}
            {formik.values.idEmployer && subdepartmentsData?.data?.length > 0 && (
              <Grid item xs={12}>
                <Autocomplete
                  options={subdepartmentsData?.data || []}
                  getOptionLabel={(option) => option.name || option.id || ""}
                  value={
                    subdepartmentsData?.data?.find((s) => s.id === formik.values.idSubdepartment) || null
                  }
                  onChange={(event, newValue) => {
                    const subdeptId = newValue?.id || "";
                    console.log("🏢 Subdepartment selected in CSV:", newValue, "ID:", subdeptId);
                    formik.setFieldValue("idSubdepartment", subdeptId);
                  }}
                  disabled={csvUploadMutation.isLoading || isLoadingSubdepartments}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={Labels.getLabel("paymentDescriptor.subdepartment", "Subdepartment (Optional)")}
                      helperText={Labels.getLabel(
                        "paymentDescriptor.csv.subdepartmentHelp",
                        "Optional. Select a subdepartment if descriptors are specific to one."
                      )}
                    />
                  )}
                />
              </Grid>
            )}

            {/* CSV Template Download */}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadCsvTemplate}
                sx={{ mb: 2 }}
              >
                {Labels.getLabel(
                  "paymentDescriptor.csv.downloadTemplate",
                  "Download CSV Template"
                )}
              </Button>
            </Grid>

            {/* CSV File Upload */}
            <Grid item xs={12}>
              <Box sx={{ border: "2px dashed #ccc", borderRadius: 2, p: 3, textAlign: "center" }}>
                <input
                  accept=".csv"
                  style={{ display: "none" }}
                  id="csv-file-upload"
                  type="file"
                  onChange={handleCsvFileChange}
                  disabled={csvUploadMutation.isLoading}
                />
                <label htmlFor="csv-file-upload">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<UploadFileIcon />}
                    disabled={csvUploadMutation.isLoading}
                  >
                    {csvFile
                      ? Labels.getLabel(
                          "paymentDescriptor.csv.upload.fileSelected",
                          "Change File"
                        )
                      : Labels.getLabel(
                          "paymentDescriptor.csv.upload.title",
                          "Upload CSV File"
                        )}
                  </Button>
                </label>
                {csvFile && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {csvFile.name} ({(csvFile.size / 1024).toFixed(2)} KB)
                  </Typography>
                )}
                {csvFileError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {csvFileError}
                  </Alert>
                )}
                <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
                  {Labels.getLabel(
                    "paymentDescriptor.csv.upload.description",
                    "CSV format: descriptorId, description, amountType, paymentAmount"
                  )}
                </Typography>
              </Box>
            </Grid>

            {/* CSV Results Display */}
            {showCsvResults && csvResults && (
              <Grid item xs={12}>
                <Alert
                  severity={csvResults.errorCount > 0 ? "warning" : "success"}
                  sx={{ mb: 2 }}
                  onClose={() => setShowCsvResults(false)}
                >
                  <Typography variant="subtitle2">
                    {Labels.getLabel("paymentDescriptor.csv.results.title", "Upload Results")}
                  </Typography>
                  <Typography variant="body2">
                    {Labels.getLabel("paymentDescriptor.csv.results.total", "Total")}: {csvResults.totalCount}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    {Labels.getLabel("paymentDescriptor.csv.results.success", "Success")}: {csvResults.successCount}
                  </Typography>
                  {csvResults.errorCount > 0 && (
                    <Typography variant="body2" color="error.main">
                      {Labels.getLabel("paymentDescriptor.csv.results.errors", "Errors")}: {csvResults.errorCount}
                    </Typography>
                  )}
                  {csvResults.errors && csvResults.errors.length > 0 && (
                    <Box sx={{ mt: 1, maxHeight: 200, overflow: "auto" }}>
                      {csvResults.errors.slice(0, 10).map((error, index) => (
                        <Typography key={index} variant="caption" display="block" color="error">
                          • Line {error.line}: {error.error}
                        </Typography>
                      ))}
                      {csvResults.errors.length > 10 && (
                        <Typography variant="caption" display="block">
                          ... and {csvResults.errors.length - 10} more errors
                        </Typography>
                      )}
                    </Box>
                  )}
                </Alert>
              </Grid>
            )}

            {/* CSV Submit Button */}
            <Grid item xs={12} pt={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleCsvSubmit}
                disabled={
                  csvUploadMutation.isLoading ||
                  !csvFile ||
                  !formik.values.idEmployer ||
                  Boolean(csvFileError)
                }
                startIcon={
                  csvUploadMutation.isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <UploadFileIcon />
                  )
                }
              >
                {csvUploadMutation.isLoading
                  ? Labels.getLabel("paymentDescriptor.csv.uploading", "Processing...")
                  : Labels.getLabel("paymentDescriptor.csv.processFile", "Process CSV")}
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
